import { useContext, useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import { popupcontext } from "../../context/Popupscontext";
import {
  useGetAllSnagsQuery,
  useExportSnagListToExcelMutation,
  useExportSnagListToPdfMutation,
  useSnagFilterValuesQuery,
} from "../../Redux/service/Snag/Snag";
import { filtrationcontext } from "../../context/Filtercontext";
import BreadCrumb from "../Header/SecondHeader";
import UsersHead from "../Users/UsersParts/UsersHead";
import FormSnag from "./FormSnag/FormSnag";
import Actions from "../antd/Table/Actions";
import { useSelector } from "react-redux";
import { Tag, Tooltip, Button, Space, Select } from "antd";
import { formatDateString } from "../../JsHelpers/DateFormat";
import SnagLocationSelectorModal from "../snagLocation/SnagLocationSelectorModal";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import useIsMobile from "../../hooks/useIsMobile";
import SnagMobile from "./mobile/SnagMobile";

const { Option } = Select;

// ✅ NEW STATUS CONSTANTS (from new backend)
const SNAG_STATUSES = {
  OPEN: "OPEN",
  FIX: "FIX",
  RECTIFY: "RECTIFY",
  VERIFY: "VERIFY",
  REOPEN: "REOPEN",
  IN_DISPUTE: "IN_DISPUTE",
  CLOSED: "CLOSED",
};

const SNAG_STATUS_LABELS = {
  OPEN: "Open",
  FIX: "Fixed",
  RECTIFY: "Rectified",
  VERIFY: "Verified",
  REOPEN: "Reopened",
  IN_DISPUTE: "In Dispute",
  CLOSED: "Closed",
};

const buildFilterQueryString = (filters) => {
  const params = new URLSearchParams();

  Object.keys(filters).forEach((key) => {
    if (filters[key]?.length) {
      params.append(key, filters[key].join(","));
    }
  });

  return params.toString();
};

const SnagList = () => {
  const isMobile = useIsMobile();
  const [snagForm, setSnagForm] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [preselectedLocation, setPreselectedLocation] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [tableFilters, setTableFilters] = useState({});

  // ✅ Edit state
  const [editingSnagId, setEditingSnagId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const { handleShow, deleteItem, setDeleteItem, setEdited, edited } =
    useContext(popupcontext);
  const [currentPage, setCurrentPage] = useState(1);
  const {
    limit,
    setLimit,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
  } = useContext(filtrationcontext);
  const user = useSelector((state) => state.users.user);

  // ✅ Subscribe to snag WebSocket events
  const snagWebSocket = useSelector((state) => state.snagWebSocket);
  const latestSnagEvent = snagWebSocket?.latestSnagEvent;
  const eventType = snagWebSocket?.eventType;
  const lastProcessedEventRef = useRef(null);

  const [sort, setSort] = useState({
    by: "",
    order: "",
  });

  // ✅ Status filter state
  const [statusFilter, setStatusFilter] = useState(null);

  // ✅ Export mutations
  const [exportToExcel, { isLoading: isExportingExcel }] =
    useExportSnagListToExcelMutation();
  const [exportToPdf, { isLoading: isExportingPdf }] =
    useExportSnagListToPdfMutation();

  const {
    data: SnagsData,
    isFetching,
    refetch,
  } = useGetAllSnagsQuery(
    {
      page: currentPage,
      limit: limit,
      search: searchQuery,
      sortby: sort.by,
      order: sort.order,
      // status: statusFilter,
      queryString: buildFilterQueryString(tableFilters),
    },
    {
      skip: !currentPage || !limit,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: filterValues } = useSnagFilterValuesQuery();

  // ✅ Auto-refetch when WebSocket events occur
  useEffect(() => {
    if (latestSnagEvent && eventType) {
      const eventId = `${latestSnagEvent.id}-${eventType}`;

      if (lastProcessedEventRef.current !== eventId) {
        lastProcessedEventRef.current = eventId;
        console.log(`📥 Snag ${eventType} event - refetching list...`);
        refetch();
      }
    }
  }, [latestSnagEvent, eventType, refetch]);

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    async function fetchdata() {
      if (edited) {
        await refetch();
        setEdited(false);
      }
      if (refresh) {
        await refetch();
        setRefresh(false);
      }
    }
    fetchdata();
  }, [edited, refresh, refetch, setEdited]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    localStorage.setItem("page", currentPage);
  }, [currentPage]);

  const FILTER_KEY_MAPPING = {
    project: "project_id",
    zone: "zone_id",
    level: "level_id",
    room: "room_id",
    assigned_to: "assigned_team_id",
    reported_by: "reported_by_id",
    status_info: "status",
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "",
        order: "",
      });
    }

    // Handle filters
    const cleanedFilters = {};

    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key].length > 0) {
        cleanedFilters[FILTER_KEY_MAPPING[key] || key] = filters[key];
      }
    });

    setTableFilters(cleanedFilters);
  };

  useEffect(() => {
    if (deleteItem.condition === true && deleteItem.value === "snag") {
      refetch();
      if (SnagsData?.data.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
        localStorage.setItem("page", currentPage);
      }
      setDeleteItem({ condition: null, value: null });
    }
  }, [
    currentPage,
    deleteItem.condition,
    deleteItem.value,
    refetch,
    setDeleteItem,
    SnagsData?.data,
  ]);

  // ✅ Export handlers
  const handleExportToExcel = async () => {
    try {
      await exportToExcel({
        search: searchQuery,
        status: statusFilter,
        sortby: sort.by,
        order: sort.order,
      }).unwrap();
      toast.success(trans("Excel file downloaded successfully"), {
        autoClose: 2000,
        rtl: localStorage.getItem("lang") === "ar",
      });
    } catch (error) {
      console.error("Export to Excel failed:", error);
      toast.error(trans("Failed to export to Excel"), {
        autoClose: 2000,
        rtl: localStorage.getItem("lang") === "ar",
      });
    }
  };

  const handleExportToPdf = async () => {
    try {
      await exportToPdf({
        search: searchQuery,
        status: statusFilter,
        sortby: sort.by,
        order: sort.order,
      }).unwrap();
      toast.success(trans("PDF file downloaded successfully"), {
        autoClose: 2000,
        rtl: localStorage.getItem("lang") === "ar",
      });
    } catch (error) {
      console.error("Export to PDF failed:", error);
      toast.error(trans("Failed to export to PDF"), {
        autoClose: 2000,
        rtl: localStorage.getItem("lang") === "ar",
      });
    }
  };

  // ✅ Handle edit snag
  const handleEditSnag = (snagId) => {
    setEditingSnagId(snagId);
    setIsEditMode(true);
    setSnagForm(true);
  };

  const columns = [
    {
      title: trans("Snag Number"),
      dataIndex: "snag_number",
      key: "snag_number",
      width: 180,
      fixed: !isRTL ? "left" : undefined,
      filters: filterValues?.filter_options?.snag_number?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Snag Type"),
      dataIndex: "snag_type",
      key: "snag_type",
      width: 180,
      render: (snag_type) => (
        <Tag color={snag_type === "EXTERNAL" ? "red" : "blue"}>{snag_type}</Tag>
      ),
      filters: filterValues?.filter_options?.snag_type?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Reported At"),
      dataIndex: "reported_at",
      key: "reported_at",
      width: 180,
      render: (reported_at) => reported_at && formatDateString(reported_at),
      sorter: true,
    },
    {
      title: trans("Project"),
      dataIndex: "project",
      key: "project",
      render: (project) => project?.name || "-",
      width: 150,
      fixed: !isRTL ? "left" : undefined,
      filters: filterValues?.filter_options?.project?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Building"),
      dataIndex: "zone",
      key: "zone",
      render: (zone) => zone?.name || "-",
      width: 150,
      fixed: !isRTL ? "left" : undefined,
      filters: filterValues?.filter_options?.zone?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("level"),
      dataIndex: "level",
      key: "level",
      render: (level) => level?.name || "-",
      width: 150,
      filters: filterValues?.filter_options?.level?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("room"),
      dataIndex: "room",
      key: "room",
      render: (room) => room?.name || "-",
      width: 150,
      filters: filterValues?.filter_options?.room?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Sector"),
      dataIndex: "sector_label",
      key: "sector",
      width: 200,
      filters: filterValues?.filter_options?.sector?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Subsystem"),
      dataIndex: "subsystem_label",
      key: "subsystem",
      render: (subsystem) => subsystem || "-",
      width: 150,
      filters: filterValues?.filter_options?.subsystem?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Severity"),
      dataIndex: "severity",
      key: "severity",
      render: (severity) =>
        (
          <Tag
            color={
              severity === "MINOR"
                ? "green"
                : severity === "MAJOR"
                  ? "yellow"
                  : severity === "CRITICAL"
                    ? "red"
                    : "blue"
            }
          >
            {severity}
          </Tag>
        ) || "-",
      width: 150,
      sorter: true,
      filters: filterValues?.filter_options?.severity?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Status"),
      dataIndex: "status_info",
      key: "status_info",
      width: 220,
      render: (status) => (
        <Tooltip title={status?.description}>
          <Tag color={status?.color}>{status?.label}</Tag>
        </Tooltip>
      ),
      filters: filterValues?.filter_options?.status?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Reported By"),
      dataIndex: "reportedBy",
      key: "reported_by",
      render: (reportedBy) => reportedBy?.name || "-",
      width: 150,
      filters: filterValues?.filter_options?.reported_by?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Assigned To"),
      dataIndex: "assignedTeam",
      key: "assigned_to",
      render: (assignedTeam) => assignedTeam?.name || "-",
      width: 150,
      filters: filterValues?.filter_options?.assigned_team?.map((item) => ({
        text: item.label,
        value: item.value,
      })),
      filterSearch: true,
    },
    {
      title: trans("Actions"),
      width: 150,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) => (
        <Actions
          snagId={record.id}
          type="snag"
          snagType={record.snag_type}
          onEdit={handleEditSnag}
        />
      ),
    },
  ];

  let finalColumns = columns;

  if (user?.attributes?.role === "consultant") {
    finalColumns = columns.filter((col) => col.dataIndex !== "snag_type");
  }

  return (
    <>
      <Helmet>
        <title>{trans("Snag List")}</title>
        <meta name="description" content={trans("Snag List")} />
      </Helmet>
      {isMobile ? (
        <SnagMobile userRole={user?.attributes?.role} />
      ) : (
        <>
          <BreadCrumb
            add={trans("Add new snag")}
            onClick={() => setLocationModalOpen(true)}
            secButton={trans("Refresh")}
            secClick={() => setRefresh(true)}
            secLoading={isFetching}
          />

          {/* ✅ UsersHead with Status Filter and Export Buttons */}
          <div style={{ position: "relative" }}>
            <UsersHead />

            {/* ✅ Status Filter and Export Buttons - Positioned absolutely in UsersHead row */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                right: 0,
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                paddingRight: "16px",
              }}
            >
              {/* ✅ Status Filter Dropdown */}
              {/* <Select
            placeholder={trans("Filter by Status")}
            allowClear
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
          >
            <Option value={SNAG_STATUSES.OPEN}>
              {trans(SNAG_STATUS_LABELS.OPEN)}
            </Option>
            <Option value={SNAG_STATUSES.FIX}>
              {trans(SNAG_STATUS_LABELS.FIX)}
            </Option>
            <Option value={SNAG_STATUSES.RECTIFY}>
              {trans(SNAG_STATUS_LABELS.RECTIFY)}
            </Option>
            <Option value={SNAG_STATUSES.VERIFY}>
              {trans(SNAG_STATUS_LABELS.VERIFY)}
            </Option>
            <Option value={SNAG_STATUSES.REOPEN}>
              {trans(SNAG_STATUS_LABELS.REOPEN)}
            </Option>
            <Option value={SNAG_STATUSES.IN_DISPUTE}>
              {trans(SNAG_STATUS_LABELS.IN_DISPUTE)}
            </Option>
            <Option value={SNAG_STATUSES.CLOSED}>
              {trans(SNAG_STATUS_LABELS.CLOSED)}
            </Option>
          </Select> */}

              {/* ✅ Export Buttons */}
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={isExportingExcel}
                disabled={isExportingExcel || isExportingPdf}
                title={trans("Export to Excel")}
              >
                {trans("Excel")}
              </Button>

              <Button
                type="default"
                icon={<FilePdfOutlined />}
                onClick={handleExportToPdf}
                loading={isExportingPdf}
                disabled={isExportingExcel || isExportingPdf}
                title={trans("Export to PDF")}
              >
                {trans("PDF")}
              </Button>
            </div>
          </div>

          <div className="bg-white-rounded-top mt-3 table-parent">
            <AntdTable
              columns={finalColumns}
              loading={isFetching}
              dataSource={SnagsData?.data || []}
              pageSize={limit}
              totalItems={SnagsData?.pagination?.total || 0}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setCurrentPage(1);
              }}
              onTableChange={handleTableChange}
              scrollX={true}
            />
          </div>
        </>
      )}
      <SnagLocationSelectorModal
        open={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
        }}
        onConfirm={(locationData) => {
          console.log("Location data confirmed:", locationData);
          setPreselectedLocation(locationData);
          setLocationModalOpen(false);
          setSnagForm(true);
        }}
      />
      <FormSnag
        snagForm={snagForm}
        setSnagForm={(value) => {
          setSnagForm(value);
          if (!value) {
            setPreselectedLocation(null);
            setEditingSnagId(null);
            setIsEditMode(false);
          }
        }}
        preselectedLocation={preselectedLocation}
        editingSnagId={editingSnagId}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default SnagList;
